/**
 * Message Creation Utilities
 * 
 * Standardized functions for creating chat messages with consistent structure
 * and proper ID management. Reduces code duplication across the ChatSidebar component.
 */

// Counter for ensuring unique IDs even when Date.now() returns the same value
let messageIdCounter = 0;

/**
 * Generates a unique message ID using timestamp + counter
 * @returns {number} Unique message ID
 */
const generateUniqueId = () => {
  return Date.now() * 1000 + (++messageIdCounter);
};

/**
 * Creates a user message object with standardized structure
 * @param {string} content - The message content
 * @param {Object} options - Additional options for the message
 * @param {number} options.id - Custom message ID (auto-generated if not provided)
 * @param {Object} options.context - Message context data
 * @param {boolean} options.hiddenUserMessage - Whether the message should be hidden
 * @param {number} messagesLength - Current messages array length for ID generation
 * @returns {Object} User message object
 */
export const createUserMessage = (content, options = {}, messagesLength = 0) => {
  return {
    id: options.id || generateUniqueId(),
    type: 'user',
    content: content.trim(),
    timestamp: new Date(),
    context: options.context,
    isHidden: options.hiddenUserMessage || false
  };
};

/**
 * Creates an agent message object with standardized structure
 * @param {string} content - The message content
 * @param {Object} options - Additional options for the message
 * @param {number} options.id - Custom message ID (auto-generated if not provided)
 * @param {Object} options.context - Message context data
 * @param {Array} options.toolsUsed - Tools used by the agent
 * @param {string} options.rationale - Agent's rationale
 * @param {string} options.action - Agent action type
 * @param {Object} options.actionData - Action-specific data
 * @param {boolean} options.isCannedResponse - Whether this is a canned response
 * @param {boolean} options.isError - Whether this is an error message
 * @param {boolean} options.isSystemMessage - Whether this is a system message
 * @param {boolean} options.isTourMessage - Whether this is a tour message
 * @param {number} options.tourStep - Tour step number
 * @param {number} options.totalTourSteps - Total tour steps
 * @param {string} options.tourType - Tour type
 * @param {Object} options.autoExpandData - Auto-expand configuration
 * @param {boolean} options.isAutoExpanding - Whether message is auto-expanding
 * @param {boolean} options.isWelcome - Whether this is a welcome message
 * @param {boolean} options.isPortalMode - Whether this is in portal mode
 * @param {boolean} options.isLoading - Whether this is a loading message
 * @param {number} messagesLength - Current messages array length for ID generation
 * @returns {Object} Agent message object
 */
export const createAgentMessage = (content, options = {}, messagesLength = 0) => {
  const baseMessage = {
    id: options.id || generateUniqueId(),
    type: 'agent',
    content,
    timestamp: new Date()
  };

  // Add optional properties only if they exist
  if (options.context) baseMessage.context = options.context;
  if (options.toolsUsed) baseMessage.toolsUsed = options.toolsUsed;
  if (options.rationale) baseMessage.rationale = options.rationale;
  if (options.action) baseMessage.action = options.action;
  if (options.actionData) baseMessage.actionData = options.actionData;
  if (options.suggestions) baseMessage.suggestions = options.suggestions;
  if (options.isCannedResponse) baseMessage.isCannedResponse = options.isCannedResponse;
  if (options.isError) baseMessage.isError = options.isError;
  if (options.isSystemMessage) baseMessage.isSystemMessage = options.isSystemMessage;
  if (options.isTourMessage) baseMessage.isTourMessage = options.isTourMessage;
  if (options.tourStep) baseMessage.tourStep = options.tourStep;
  if (options.totalTourSteps) baseMessage.totalTourSteps = options.totalTourSteps;
  if (options.tourType) baseMessage.tourType = options.tourType;
  if (options.autoExpandData) baseMessage.autoExpandData = options.autoExpandData;
  if (options.isAutoExpanding) baseMessage.isAutoExpanding = options.isAutoExpanding;
  if (options.isWelcome) baseMessage.isWelcome = options.isWelcome;
  if (options.isPortalMode) baseMessage.isPortalMode = options.isPortalMode;
  if (options.isLoading) baseMessage.isLoading = options.isLoading;
  if (options.showInlineButtons) baseMessage.showInlineButtons = options.showInlineButtons;
  if (options.inlineButtons) baseMessage.inlineButtons = options.inlineButtons;
  if (options.disableExpansion) baseMessage.disableExpansion = options.disableExpansion;

  return baseMessage;
};

/**
 * Creates a tour message object (specialized agent message)
 * @param {string} content - The message content
 * @param {Object} options - Tour-specific options
 * @param {number} options.stepIndex - Current step index (0-based)
 * @param {number} options.totalSteps - Total number of steps
 * @param {string} options.tourType - Type of tour
 * @param {number} messagesLength - Current messages array length for ID generation
 * @returns {Object} Tour message object
 */
export const createTourMessage = (content, options = {}, messagesLength = 0) => {
  return createAgentMessage(content, {
    id: options.id || generateUniqueId(),
    isTourMessage: true,
    tourStep: options.stepIndex + 1,
    totalTourSteps: options.totalSteps,
    tourType: options.tourType
  }, messagesLength);
};

/**
 * Creates an error message object (specialized agent message)
 * @param {string} content - The error message content
 * @param {Object} options - Additional options
 * @param {number} messagesLength - Current messages array length for ID generation
 * @returns {Object} Error message object
 */
export const createErrorMessage = (content, options = {}, messagesLength = 0) => {
  return createAgentMessage(content, {
    ...options,
    isError: true
  }, messagesLength);
};

/**
 * Creates a system message object (specialized agent message)
 * @param {string} content - The system message content
 * @param {Object} options - Additional options
 * @param {number} messagesLength - Current messages array length for ID generation
 * @returns {Object} System message object
 */
export const createSystemMessage = (content, options = {}, messagesLength = 0) => {
  return createAgentMessage(content, {
    ...options,
    isSystemMessage: true
  }, messagesLength);
};

/**
 * Creates a choice card success message (specialized agent message)
 * @param {Object} selectedCard - The selected choice card
 * @param {number} messagesLength - Current messages array length for ID generation
 * @returns {Object} Choice card success message object
 */
export const createChoiceCardSuccessMessage = (selectedCard, messagesLength = 0) => {
  return createAgentMessage(
    `✅ **Perfect choice!** Taking you to "${selectedCard.title}" now.`,
    {
      isCannedResponse: true
    },
    messagesLength
  );
};

/**
 * Creates a mock response message (specialized agent message)
 * @param {Object} mockResponse - The mock response object
 * @param {number} messagesLength - Current messages array length for ID generation
 * @returns {Object} Mock response message object
 */
export const createMockResponseMessage = (mockResponse, messagesLength = 0) => {
  const options = {
    toolsUsed: mockResponse.toolsUsed,
    action: mockResponse.action,
    actionData: mockResponse.actionData
  };
  
  // Add suggestions if they exist
  if (mockResponse.suggestions) {
    options.suggestions = mockResponse.suggestions;
  }
  
  // Add rationale if it exists
  if (mockResponse.rationale) {
    options.rationale = mockResponse.rationale;
  }
  
  return createAgentMessage(mockResponse.content, options, messagesLength);
};

/**
 * Creates a message object from database format
 * @param {Object} dbMessage - Database message object (supports both 'role' and 'type' fields)
 * @param {number} index - Message index for ID generation
 * @returns {Object} Frontend message object
 */
export const createMessageFromDB = (dbMessage, index = 0) => {
  // Handle both 'role' (Redis format) and 'type' (DB format) fields
  const messageRole = dbMessage.role || dbMessage.type;
  const isUser = messageRole === 'user';
  const baseMessage = {
    id: dbMessage.id || generateUniqueId(),
    type: isUser ? 'user' : 'agent',
    content: dbMessage.content,
    timestamp: new Date(dbMessage.timestamp),
  };

  // Add agent-specific properties
  if (!isUser) {
    // Handle toolsUsed - ensure it's always an array
    if (dbMessage.tool_results) {
      if (Array.isArray(dbMessage.tool_results)) {
        baseMessage.toolsUsed = dbMessage.tool_results;
      } else if (dbMessage.tool_results.tools_used && Array.isArray(dbMessage.tool_results.tools_used)) {
        baseMessage.toolsUsed = dbMessage.tool_results.tools_used;
      } else {
        // Fallback: extract tool names from object or set empty array
        baseMessage.toolsUsed = [];
      }
    }
    
    // Handle recommendations/suggestions
    if (dbMessage.recommendations) {
      if (Array.isArray(dbMessage.recommendations)) {
        baseMessage.suggestions = dbMessage.recommendations;
      } else {
        baseMessage.suggestions = [];
      }
    }
    
    // Handle action data
    if (dbMessage.action_data) {
      baseMessage.action = dbMessage.action_data.type;
      baseMessage.actionData = dbMessage.action_data;
    }
  }

  // Add context if available
  if (dbMessage.context) baseMessage.context = dbMessage.context;

  return baseMessage;
};
